/**
 * SA-01 фаза 1 — экран «Закрытие сезона».
 *
 * Задача агронома в конце года: посевы, по которым сезон фактически кончился,
 * так и висят «Растёт» — потому что уборку не отметили работой, или данные
 * приехали импортом. Экран показывает всё незакрытое одним списком, даёт
 * отметить галочками и закрыть выбранные одним действием.
 *
 * Сознательно НЕ автомат: автоматическое закрытие от операций делает домен
 * операций. Здесь — ручная подчистка того, что автомат не закрыл.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, TriangleAlert } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getSowingStatusInfo } from '@/components/fields/sowingStatus';
import { listSeasons } from '@/lib/season/api';
import {
  CLOSING_STATUSES,
  useCloseSowings,
  useOpenSowings,
  type CloseSowingsResponse,
  type OpenSowingRow,
  type SowingStatusCode,
} from '@/lib/sowings/statusApi';

function fmtHa(v: number | null): string {
  if (v == null) return '—';
  return `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`;
}

function occupancyLabel(row: OpenSowingRow): string {
  if (row.occupancy_type === 'fallow') return row.fallow_name ?? 'Пар';
  return row.crop_name ?? 'Культура';
}

export function SeasonClosingPage() {
  const seasons = useQuery({
    queryKey: ['seasons', 'all'],
    queryFn: () => listSeasons(),
    staleTime: 5 * 60_000,
  });

  const [seasonId, setSeasonId] = useState<string | null>(null);
  useEffect(() => {
    if (seasonId) return;
    const cur = seasons.data?.find((s) => s.isCurrent) ?? seasons.data?.[0];
    if (cur) setSeasonId(cur.id);
  }, [seasons.data, seasonId]);

  const open = useOpenSowings(seasonId, null);
  const close = useCloseSowings(seasonId);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // AK-513 шаг 2: дефолт «Убрано» вместо снятого из канона «Закрыт»
  // (ADR-059 §D7) — состояние подавляющего большинства висящих занятий;
  // «Списано» требует осознанного выбора.
  const [targetStatus, setTargetStatus] = useState<SowingStatusCode>('harvested');
  const [reason, setReason] = useState('');
  const [report, setReport] = useState<CloseSowingsResponse | null>(null);

  // Смена сезона обнуляет выбор — иначе закроем не то, что видим.
  useEffect(() => {
    setSelected(new Set());
    setReport(null);
  }, [seasonId]);

  const rows = open.data ?? [];
  const allSelected = rows.length > 0 && selected.size === rows.length;

  const selectedArea = useMemo(
    () =>
      rows
        .filter((r) => selected.has(r.sowing_id))
        .reduce((acc, r) => acc + (r.area_ha ?? 0), 0),
    [rows, selected],
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.sowing_id)));
  };

  const submit = () => {
    if (!seasonId || selected.size === 0) return;
    close.mutate(
      {
        sowing_ids: [...selected],
        status: targetStatus,
        reason: reason.trim() || null,
      },
      {
        onSuccess: (resp) => {
          setReport(resp);
          setSelected(new Set());
          setReason('');
        },
      },
    );
  };

  return (
    <AppShell>
      <div className="flex h-full flex-col gap-4">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground">
            Закрытие сезона
          </h1>
          <p className="text-sm text-muted-foreground">
            Посевы, по которым сезон уже кончился, а статус так и остался
            рабочим. Отметьте нужные и закройте одним действием.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Что закрываем</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-full max-w-xs space-y-1.5">
                <Label>Сезон</Label>
                <Select
                  value={seasonId ?? ''}
                  onValueChange={setSeasonId}
                  disabled={seasons.isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите сезон" />
                  </SelectTrigger>
                  <SelectContent>
                    {(seasons.data ?? []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.year}
                        {s.isCurrent ? ' (текущий)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full max-w-xs space-y-1.5">
                <Label>Каким статусом закрыть</Label>
                <Select
                  value={targetStatus}
                  onValueChange={(v) => setTargetStatus(v as SowingStatusCode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLOSING_STATUSES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {getSowingStatusInfo(code)?.label ?? code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full max-w-sm space-y-1.5">
                <Label htmlFor="closing-reason">
                  Почему закрываем (необязательно)
                </Label>
                <Input
                  id="closing-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Убрали, работы не отмечали"
                />
              </div>
            </div>

            {!seasonId ? (
              <Alert>
                <AlertDescription>
                  Выберите сезон, чтобы увидеть незакрытые посевы.
                </AlertDescription>
              </Alert>
            ) : open.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : open.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  Не удалось загрузить незакрытые посевы.
                </AlertDescription>
              </Alert>
            ) : rows.length === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  В этом сезоне закрывать нечего — все посевы завершены.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Отметить все"
                          />
                        </TableHead>
                        <TableHead>Поле</TableHead>
                        <TableHead>Хозяйство</TableHead>
                        <TableHead>Что растёт</TableHead>
                        <TableHead>Площадь</TableHead>
                        <TableHead>Статус</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.sowing_id}>
                          <TableCell>
                            <Checkbox
                              checked={selected.has(r.sowing_id)}
                              onCheckedChange={() => toggle(r.sowing_id)}
                              aria-label={`Отметить ${r.field_name}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {r.field_name}
                            {r.zone_name ? (
                              <span className="text-muted-foreground">
                                {' '}
                                · {r.zone_name}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {r.farm_name ?? '—'}
                          </TableCell>
                          <TableCell>{occupancyLabel(r)}</TableCell>
                          <TableCell>{fmtHa(r.area_ha)}</TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={getSowingStatusInfo(r.status)?.className}
                            >
                              {r.status_label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    Отмечено: {selected.size} из {rows.length}
                    {selected.size > 0 ? ` · ${fmtHa(selectedArea)}` : ''}
                  </p>
                  <Button
                    onClick={submit}
                    disabled={selected.size === 0 || close.isPending}
                  >
                    {close.isPending ? 'Закрываем…' : 'Закрыть выбранные'}
                  </Button>
                </div>

                {close.isError && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Не удалось закрыть посевы. Попробуйте ещё раз.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {report && (
              <Alert variant={report.skipped > 0 ? 'default' : undefined}>
                {report.skipped > 0 ? (
                  <TriangleAlert className="h-4 w-4" />
                ) : (
                  <Info className="h-4 w-4" />
                )}
                <AlertDescription>
                  <div>
                    Закрыто: {report.closed}
                    {report.skipped > 0 ? ` · пропущено: ${report.skipped}` : ''}
                  </div>
                  {report.skipped > 0 && (
                    <ul className="mt-1 list-disc pl-4 text-sm">
                      {report.results
                        .filter((r) => !r.ok)
                        .map((r) => (
                          <li key={r.sowing_id}>
                            {r.field_name ?? 'Посев'} — {r.error}
                          </li>
                        ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

export default SeasonClosingPage;
